import React, { useMemo } from 'react';
import {
  LEAVE_FOR_PERSONAL_CIRCUMSTANCES_SUB_TYPES,
  LEAVE_TYPES_ALIASES,
  SICK_DAY_SUB_TYPES,
} from '../../../../shared/leave/constants';
import { maternitySubtypeOptionsForEmployee } from '../maternityLeaveEligibility';
import { isPolishEcEmployee, paidLeaveSubtypeOptions } from '../paidLeaveSubtypeOptions';
import { EnumDropdown, EnumDropdownProps, useInputHelper } from 'framework/form';
import { useDidUpdateEffect } from 'framework/utils';

const SubTypeReasonDropdown = ({
  sourceType,
  employee,
  label = 'Reason',
  ...props
}: Omit<EnumDropdownProps, 'options'> & {
  sourceType: string;
  employee?: { officeId?: string | number; contractType?: string };
}) => {
  const { getValue, getRawOnChangeCallback } = useInputHelper({ source: sourceType });
  const { getValue: getSubType, getRawOnChangeCallback: getSubTypeOnChange } = useInputHelper({ source: props.source });
  const leaveType = getValue();
  const onChangeCallback = getRawOnChangeCallback() || (() => {});
  const subTypeOnChange = getSubTypeOnChange() || (() => {});

  const isPolishAndEc = isPolishEcEmployee(employee);

  const inputLabel =
    leaveType === LEAVE_TYPES_ALIASES.PAID_LEAVE || leaveType === LEAVE_TYPES_ALIASES.MATERNITY_LEAVE
      ? 'Subtype'
      : label;

  const options = useMemo(() => {
    if (leaveType === LEAVE_TYPES_ALIASES.SICK_DAY) {
      return SICK_DAY_SUB_TYPES;
    }
    if (leaveType === LEAVE_TYPES_ALIASES.LEAVE_FOR_PERSONAL_CIRCUMSTANCES) {
      return LEAVE_FOR_PERSONAL_CIRCUMSTANCES_SUB_TYPES;
    }
    if (leaveType === LEAVE_TYPES_ALIASES.MATERNITY_LEAVE) {
      return maternitySubtypeOptionsForEmployee(employee?.officeId, employee?.contractType);
    }
    if (leaveType === LEAVE_TYPES_ALIASES.PAID_LEAVE) {
      return paidLeaveSubtypeOptions(isPolishAndEc);
    }
    return {};
  }, [employee, isPolishAndEc, leaveType]);

  const optionsKey = useMemo(() => Object.keys(options).sort().join(','), [options]);

  useDidUpdateEffect(() => {
    onChangeCallback({
      [props.source]: null,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveType]);

  useDidUpdateEffect(() => {
    if (leaveType === LEAVE_TYPES_ALIASES.PAID_LEAVE && !isPolishAndEc && getSubType() === 'DAY_ON_DEMAND') {
      subTypeOnChange({ [props.source]: null });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPolishAndEc, leaveType]);

  return (
    <EnumDropdown
      key={`${leaveType}-${optionsKey}`}
      {...props}
      options={options}
      placeholder="Choose Reason"
      label={inputLabel}
    />
  );
};

export default SubTypeReasonDropdown;
